import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IonButton,
  useIonToast,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonContent,
  IonIcon,
  IonImg,
} from '@ionic/react';
import { ellipsisVertical, shareSocial, checkmarkCircle, searchOutline } from 'ionicons/icons';
import { useDispatch, useSelector } from 'react-redux';
import { ICasinoSearchData, IState, useAppSelector } from 'appRedux/createStore';
import { SlotCompareReduxCommandCreator } from 'appRedux/actions/slotCompareCommandCreator';
import { SlotReduxCommandCreator } from 'appRedux/actions/slotCommandCreator';
import MetricHeader, { timeframes } from './MetricHeader';
import ComparisonViewHeader from './ComparisonViewHeader';
import { CasinoService, IRequestAdvancedTrendsParam } from 'services/casino';
import { Subscription } from 'rxjs';
import { InsightFilterEnum } from 'appRedux/models/favoritesModels';
import { ICasinoSchema, ISearchInfo } from 'appRedux/models/casinoModels';
import { LikeButton, isIncludedInFavorites } from 'pages/Favorites/components/LikeButton';
import {
  getCleanGameName,
  getCleanThemeName,
  OrderByEnum,
  toCapitalizedWords,
} from 'common/common';
import TextMarquee from 'components/TextMarquee';
import { parseValue } from 'common/include';
import { shouldShowPop } from 'lib/showPop';
import { IGameData } from './types';
import { allMetrics } from './compareMetrics';
import { buildRtpTrendValues, shouldShowCompareRtpTrend } from './compareTrendRtp';
import { isEqual } from 'lodash';
import SmartLoading from 'components/SmartLoading';
import GradientLineChart from '../Detail/GradientLineChart';
import { ViewMode } from '../../..';
import useFavorites from 'common/reactHooks/useFavorites';
import usePlaylist, { isIncludedInPlaylist } from 'common/reactHooks/usePlaylist';
import { FavoriteIcon } from 'components/FavoriteIcon/FavoriteIcon';
import { PlaylistIcon } from 'components/PlaylistIcon/PlaylistIcon';
import {
  selectCasinoId,
  selectFavoritesList,
  selectPlaylist,
  selectUserId,
} from 'appRedux/selectors';
import { featureFlags } from 'config/featureFlags';
import { InfoIcon } from 'components/InfoIcon/InfoIcon';
import { MACHINE_MENU_POPOVER_WIDTH } from 'components/MachineActionsMenu/menuStyles';
import {
  egmDetailKey,
  insightCardName,
  insightCardTitle,
  insightLabel,
  isInsightCardRow,
  matchesEgmDetailId,
  searchRowDetailId,
} from 'services/insightCasino';

type Props = {
  viewMode: ViewMode;
  // Reports the natural pixel height of the rendered comparison content so the
  // drawer can open just far enough to show every selected EGM (capped at 100%).
  onContentHeightChange?: (height: number) => void;
  onViewSlot?: (slotNumber: string) => void;
};
export { allMetrics } from './compareMetrics';

const CompareAdvancedView: React.FC<Props> = ({ viewMode, onViewSlot, onContentHeightChange }) => {
  const casinoSvc = new CasinoService();
  const itemsPerPage = 50;

  const casinoSchema: ICasinoSchema = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema
  ) as ICasinoSchema;

  const isProUser = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.isProUser
  ) as boolean;

  // Per-casino POP visibility (casino.showPOP = 1); hidden by default.
  const showPop = shouldShowPop(casinoSchema);

  const {
    compareSortBy: orderInsight,
    timeFrameOrderBy,
    timeFramePrefix,
    selectedSlots,
    selectedInsight,
    selectedInsights,
  } = useAppSelector((state) => state.app.persistedState.compareSlotsState);

  const dispatch = useDispatch();
  const slotCompareCommands = SlotCompareReduxCommandCreator(dispatch);
  const slotCommands = SlotReduxCommandCreator(dispatch);

  const userId = useSelector(selectUserId);
  const casinoId = useSelector(selectCasinoId);
  const favoritesList = useSelector(selectFavoritesList);
  const playlist = useSelector(selectPlaylist);
  const { handleInsertFavorite, handleDeleteFavorite } = useFavorites({});
  const { handleInsertPlaylist, handleDeletePlaylist } = usePlaylist();
  const isPlaylistEnabled = featureFlags.playlist;
  const casinoSearchData = useSelector<IState>(
    (state) => state.app.persistedState.casinoSearchData
  ) as ICasinoSearchData | undefined;

  const [popoverPosition, setPopoverPosition] = useState<{ x: number; y: number } | null>(null);
  const [activeSlotNumber, setActiveSlotNumber] = useState<string | null>(null);

  const closeMenu = () => {
    setPopoverPosition(null);
    setActiveSlotNumber(null);
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, slotNumber: string) => {
    e.stopPropagation();
    const targetEl = e.currentTarget;
    const rect = targetEl.getBoundingClientRect();
    const popoverHeight = 360;
    const safeMargin = 16;

    let x = rect.left - MACHINE_MENU_POPOVER_WIDTH - 4;
    if (x < safeMargin) x = safeMargin;

    let y = rect.top;
    y = Math.min(y, window.innerHeight - popoverHeight - safeMargin);
    y = Math.max(y, safeMargin);

    setPopoverPosition({ x, y });
    setActiveSlotNumber(slotNumber);
  };

  const handleRemoveFromCompare = (slotNumber: string) => {
    const toRemove = Number(slotNumber);
    const newCompareSelected = (selectedSlots || []).filter((s) => s !== toRemove);
    slotCompareCommands.setSlotNumbers({ slotNumbers: newCompareSelected });
    closeMenu();
  };

  const handleToggleFavorite = (slotNumber: string) => {
    if (!userId || !casinoId) {
      closeMenu();
      return;
    }
    if (isIncludedInFavorites(slotNumber, favoritesList)) {
      handleDeleteFavorite(slotNumber, userId, String(casinoId));
    } else {
      handleInsertFavorite(slotNumber, userId, String(casinoId));
    }
    closeMenu();
  };

  const handleTogglePlaylist = (slotNumber: string) => {
    if (!userId || !casinoId) {
      closeMenu();
      return;
    }
    if (isIncludedInPlaylist(slotNumber, playlist)) {
      handleDeletePlaylist(slotNumber, userId, String(casinoId));
    } else {
      handleInsertPlaylist(slotNumber, userId, String(casinoId));
    }
    closeMenu();
  };

  const handleViewDetails = (slotNumber: string) => {
    if (onViewSlot) onViewSlot(slotNumber);
    closeMenu();
  };

  const handleShare = async (slotNumber: string) => {
    const data = hotDataList.find((d) => matchesEgmDetailId(d, slotNumber));
    const title = data
      ? isInsightCardRow(data)
        ? insightCardName(data) // blank name stays blank (no label fallback)
        : toCapitalizedWords(getCleanGameName(data.game_name))
      : `Slot ${slotNumber}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text: title, url: window.location.href });
      }
    } catch {
      /* dismissed */
    }
    closeMenu();
  };

  const applySearchFilter = (data: IGameData, kind: 'title' | 'theme') => {
    const searchData: ISearchInfo[] = casinoSearchData?.data ?? [];
    const titleKey = getCleanGameName(data.game_name);
    const themeKey =
      (data.game_theme && data.game_theme.trim()) || getCleanThemeName(data.game_name);
    let filterName = '';
    let matches: ISearchInfo[] = [];
    if (kind === 'title') {
      filterName = titleKey;
      matches = searchData.filter((d) => d.game_name === titleKey);
    } else {
      if (!themeKey) return;
      filterName = themeKey;
      matches = searchData.filter((d) => d.theme === themeKey);
    }
    // Insight search rows: mnum. Legacy: slot_number. Never floorLocation.
    const slotNumbers = matches
      .map((m) => searchRowDetailId(m))
      .filter((n): n is number => n !== null);
    if (slotNumbers.length === 0) {
      const id = Number(egmDetailKey(data));
      if (Number.isFinite(id)) slotNumbers.push(id);
    }
    slotCommands.SetSearchMode(true);
    slotCommands.SelectBankFilter(false);
    slotCommands.SelectedSlotsName(filterName);
    slotCommands.SelectedSlots(slotNumbers.map(String) as unknown as string[]);
  };

  const handleSearchByTitle = (slotNumber: string) => {
    const data = hotDataList.find((d) => matchesEgmDetailId(d, slotNumber));
    if (data) applySearchFilter(data, 'title');
    closeMenu();
  };

  const handleSearchByTheme = (slotNumber: string) => {
    const data = hotDataList.find((d) => matchesEgmDetailId(d, slotNumber));
    if (data) applySearchFilter(data, 'theme');
    closeMenu();
  };

  const [isListReportLoading, setListReportLoading] = useState(false);
  const [hotDataList, setHotDataList] = useState<IGameData[]>([]);
  const [totalReportCount, setTotalReportCount] = useState<number>(0);

  const [isEndReached, setIsEndReached] = useState(false);
  const ionInfiniteScrollRef = useRef<HTMLIonInfiniteScrollElement>(null);
  // Wraps the rendered comparison rows; its offsetHeight is the natural content
  // height reported to the drawer so it can size itself to the selection.
  const contentMeasureRef = useRef<HTMLDivElement>(null);
  const [presentToast] = useIonToast();
  const firstRenderRef = useRef(true);

  const [casinoParams, setCasinoParams] = useState<IRequestAdvancedTrendsParam>({
    casinoFk: casinoSchema?.kpCasinoPk,
    orderByField: '',
    orderby: OrderByEnum.Asc,
    insightFilter: '{1,2,3,4,5,6,7,8,11,12,13}',
    timeframe: timeFramePrefix,
    limit: itemsPerPage,
    pageNumber: 0,
    slotNumbers: '',
  });

  const handleInputChange = (param: keyof IRequestAdvancedTrendsParam, value: string | number) => {
    setCasinoParams((prevParams) => ({
      ...prevParams,
      [param]: value,
    }));
  };

  const fetchAdvancedTrends = (queryParams: IRequestAdvancedTrendsParam): Subscription => {
    if (queryParams.pageNumber === 0) {
      setListReportLoading(true);
    }

    return casinoSvc.GetAdvancedListReport(queryParams).subscribe({
      next(casinoResponse) {
        if (casinoResponse) {
          setHotDataList((prevItems) =>
            queryParams.pageNumber === 0
              ? casinoResponse.DataSet
              : [...prevItems, ...casinoResponse.DataSet]
          );
          setTotalReportCount(casinoResponse.totalCount || 0);
          if (casinoResponse.DataSet?.length < itemsPerPage) {
            setIsEndReached(true);
          }
        }
        void ionInfiniteScrollRef.current?.complete();
      },
      error() {
        setListReportLoading(false);
        presentToast({
          position: 'top',
          duration: 5000,
          color: 'danger',
          message: 'Failed to retrieve casino report.',
        }).catch(() => null);
        void ionInfiniteScrollRef.current?.complete();
      },
      complete() {
        setListReportLoading(false);
      },
    });
  };

  const handleFetchMore = () => {
    handleInputChange('pageNumber', casinoParams.pageNumber + 1);
  };
  const lastParamsRef = useRef<IRequestAdvancedTrendsParam | null>(null);

  const memoizedCasinoParams = useMemo(
    () => casinoParams,
    [
      casinoParams.casinoFk,
      casinoParams.orderByField,
      casinoParams.orderby,
      casinoParams.insightFilter,
      casinoParams.timeframe,
      casinoParams.pageNumber,
      casinoParams.slotNumbers,
    ]
  );

  useEffect(() => {
    if (!firstRenderRef.current) {
      const isSameAsLast = isEqual(lastParamsRef.current, memoizedCasinoParams);

      if (!isSameAsLast) {
        lastParamsRef.current = memoizedCasinoParams;
        const listReportSubscription = fetchAdvancedTrends(memoizedCasinoParams);
        return () => listReportSubscription.unsubscribe();
      }
    } else {
      firstRenderRef.current = false;
    }
  }, [memoizedCasinoParams]);

  useEffect(() => {
    if (orderInsight) {
      handleInputChange('orderByField', orderInsight);
      handleInputChange('pageNumber', 0);
    }
  }, [orderInsight]);

  useEffect(() => {
    if (timeFramePrefix || timeFrameOrderBy) {
      handleInputChange('timeframe', timeFramePrefix);
      handleInputChange('orderby', timeFrameOrderBy);
      handleInputChange('pageNumber', 0);
    }
  }, [timeFramePrefix, timeFrameOrderBy]);

  useEffect(() => {
    handleInputChange('slotNumbers', selectedSlots.length > 0 ? selectedSlots.join(',') : '');
    handleInputChange('pageNumber', 0);
  }, [selectedSlots]);

  // Publish the rendered content height (rows + headers) up to the drawer. A
  // ResizeObserver keeps it current as rows load, get removed, or the layout
  // reflows. Guard against re-reporting an unchanged value to avoid parent
  // render churn.
  const lastReportedHeightRef = useRef<number>(-1);
  useEffect(() => {
    if (!onContentHeightChange) return;
    const el = contentMeasureRef.current;
    if (!el) return;
    const report = () => {
      const h = el.offsetHeight;
      if (h !== lastReportedHeightRef.current) {
        lastReportedHeightRef.current = h;
        onContentHeightChange(h);
      }
    };
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onContentHeightChange, isListReportLoading, hotDataList]);

  const activeInsightIds =
    selectedInsights && selectedInsights.length > 0
      ? selectedInsights
      : [InsightFilterEnum.ReturnToPlayer, InsightFilterEnum.Jackpot, selectedInsight].filter(
          (v): v is string => Boolean(v)
        );

  const metrics = allMetrics
    .filter((m) => activeInsightIds.includes(m.id))
    .sort((a, b) => activeInsightIds.indexOf(a.id) - activeInsightIds.indexOf(b.id));

  // Display-only RTP trend overlay (GradientLineChart). Hidden unless:
  // 1) RTP is among the selected Compare metrics (otherwise topOffset falls
  //    back to 22% and the pink line sits on Jackpots — Miller screenshot), and
  // 2) every compared machine has ≥2 positive RTP values (drawable line).
  const rtpIndex = metrics.findIndex(({ id }) => id === InsightFilterEnum.ReturnToPlayer);
  const showCompareRtpTrend = shouldShowCompareRtpTrend({
    slots: hotDataList,
    rtpMetricSelected: rtpIndex >= 0,
  });
  const trendTopOffset =
    rtpIndex >= 0
      ? `${Math.min(((2 * rtpIndex + 1) / (2 * metrics.length)) * 100 + 6, 95)}%`
      : '22%';

  return (
    <IonContent 
      className="compare-stats-view compare-style-v2"
      scrollY={true} 
      scrollEvents={true}
      forceOverscroll={false}
      style={{
        height: '100%',
        '--overflow': 'auto',
        '--offset-bottom': '0px',
      }}
    >
      {isListReportLoading && <SmartLoading loading={isListReportLoading} />}
      {!isListReportLoading && (
        <div ref={contentMeasureRef}>
          <ComparisonViewHeader />
          {viewMode === ViewMode.ComparePortrait && (
            <div className="compare-portrait-wrapper" style={{ overflowX: 'auto', width: '100%' }}>
              <div style={{ minWidth: '990px' }}>
                <MetricHeader />
                {hotDataList.map((data, index) => (
                  <div key={`cp-${index}`} className="compare-grid-row compare-data-row">
                    <div className="cg-cell cg-game">
                      <div className="game-card game-card-info">
                        <div className="game-card__info">
                          <div
                            className="game-card__header"
                            role="button"
                            onClick={() => onViewSlot && onViewSlot(egmDetailKey(data))}
                          >
                            <TextMarquee
                              className="game__name"
                              text={
                                isInsightCardRow(data)
                                  ? insightCardName(data)
                                  : toCapitalizedWords(getCleanGameName(data?.game_name))
                              }
                            />
                            <div className="game-card__theme">
                              {isInsightCardRow(data)
                                ? insightCardTitle(data)
                                : toCapitalizedWords(
                                  getCleanThemeName(data?.game_theme || data?.game_name)
                                )}
                            </div>
                          </div>

                          <div className="game-card__meta game-card__compare-view">
                            {isInsightCardRow(data) ? insightLabel(data) : parseInt(data.slot_number)}
                            {showPop && data.pop_spins && (
                              <>
                                <span className="dot-box">·</span>
                                {`POP ${parseValue(parseInt(data.pop_spins), 'pop')}`}
                              </>
                            )}
                            <LikeButton
                              slotNumber={egmDetailKey(data)}
                              fontSize="small"
                              className="like-button"
                            />
                          </div>

                          <div className="game-card__actions">
                            <div
                              className="compare-menu-icon-btn"
                              role="button"
                              aria-label="Open actions menu"
                              onClick={(e) => openMenu(e, egmDetailKey(data))}
                            >
                              <IonIcon icon={ellipsisVertical} />
                            </div>
                            {selectedSlots.length > 1 && (
                              <IonButton
                                size="small"
                                className="compare-card__remove-btn"
                                onClick={() => handleRemoveFromCompare(egmDetailKey(data))}
                              >
                                Remove
                              </IonButton>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="cg-divider" />

                    <div className="cg-cell cg-insight">
                      {metrics.map(({ id, label }) => (
                        <div
                          key={id}
                          className={`insight-metric${
                            id === orderInsight ? ' highlighted-insight' : ''
                          }`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => slotCompareCommands.setSortBy({ compareSortBy: id })}
                        >
                          {label}
                        </div>
                      ))}
                    </div>

                    {timeframes.map((tf) => {
                      const isActive = tf.prefix === timeFramePrefix;
                      return (
                        <React.Fragment key={tf.field}>
                          <div className="cg-divider" />
                          <div className={`cg-cell cg-tf${isActive ? ' cg-tf--active' : ''}`}>
                            {metrics.map(({ id }) => {
                              const value = data.insights[id]?.[`${tf.prefix}insight_value`];
                              const isHighlighted = id === orderInsight && isActive;
                              return (
                                <div
                                  key={id}
                                  className={`highlight-container${isHighlighted ? ' highlighted-background' : ''}`}
                                >
                                  <span
                                    className={`metric-color${isHighlighted ? ' highlighted-timeframe' : ''}${!value ? ' metric-color--empty' : ''}`}
                                  >
                                    {value ? parseValue(value, id) : '—'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </React.Fragment>
                      );
                    })}
                    {showCompareRtpTrend && (
                      <GradientLineChart
                        trendValues={buildRtpTrendValues(data)}
                        keyName={egmDetailKey(data)}
                        screenWidth={110}
                        isLandscape={false}
                        insightType={InsightFilterEnum.ReturnToPlayer}
                        visibleInsights={[InsightFilterEnum.ReturnToPlayer]}
                        postionLeft={310}
                        topOffset={trendTopOffset}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {viewMode === ViewMode.CompareLandscape && (
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <div style={{ minWidth: '990px', paddingBottom: '56px' }}>
                <MetricHeader />
                {hotDataList.map((data, index) => (
                  <div key={index} className="compare-grid-row compare-data-row">
                    <div className="cg-cell cg-game">
                      <div className="game-card game-card-info">
                        <div className="game-card__info">
                          <div
                            className="game-card__header"
                            role="button"
                            onClick={() => onViewSlot && onViewSlot(egmDetailKey(data))}
                          >
                            <TextMarquee
                              className="game__name"
                              text={
                                isInsightCardRow(data)
                                  ? insightCardName(data)
                                  : toCapitalizedWords(getCleanGameName(data?.game_name))
                              }
                            />
                            <div className="game-card__theme">
                              {isInsightCardRow(data)
                                ? insightCardTitle(data)
                                : toCapitalizedWords(
                                  getCleanThemeName(data?.game_theme || data?.game_name)
                                )}
                            </div>
                          </div>

                          <div className="game-card__meta game-card__compare-view">
                            {isInsightCardRow(data) ? insightLabel(data) : parseInt(data.slot_number)}
                            {showPop && data.pop_spins && (
                              <>
                                <span className="dot-box">·</span>
                                {`POP ${parseValue(parseInt(data.pop_spins), 'pop')}`}
                              </>
                            )}
                            <LikeButton
                              slotNumber={egmDetailKey(data)}
                              fontSize="small"
                              className="like-button"
                            />
                          </div>

                          <div className="game-card__actions">
                            <div
                              className="compare-menu-icon-btn"
                              role="button"
                              aria-label="Open actions menu"
                              onClick={(e) => openMenu(e, egmDetailKey(data))}
                            >
                              <IonIcon icon={ellipsisVertical} />
                            </div>
                            {selectedSlots.length > 1 && (
                              <IonButton
                                size="small"
                                className="compare-card__remove-btn"
                                onClick={() => handleRemoveFromCompare(egmDetailKey(data))}
                              >
                                Remove
                              </IonButton>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="cg-divider" />

                    <div className="cg-cell cg-insight">
                      {metrics.map(({ id, label }) => (
                        <div
                          key={id}
                          className={`insight-metric${
                            id === orderInsight ? ' highlighted-insight' : ''
                          }`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => slotCompareCommands.setSortBy({ compareSortBy: id })}
                        >
                          {label}
                        </div>
                      ))}
                    </div>

                    {timeframes.map((tf) => {
                      const isActive = tf.prefix === timeFramePrefix;
                      return (
                        <React.Fragment key={tf.field}>
                          <div className="cg-divider" />
                          <div className={`cg-cell cg-tf${isActive ? ' cg-tf--active' : ''}`}>
                            {metrics.map(({ id }) => {
                              const value = data.insights[id]?.[`${tf.prefix}insight_value`];
                              const isHighlighted = id === orderInsight && isActive;
                              return (
                                <div
                                  key={id}
                                  className={`highlight-container${isHighlighted ? ' highlighted-background' : ''}`}
                                >
                                  <span
                                    className={`metric-color${isHighlighted ? ' highlighted-timeframe' : ''}${!value ? ' metric-color--empty' : ''}`}
                                  >
                                    {value ? parseValue(value, id) : '—'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </React.Fragment>
                      );
                    })}
                    {showCompareRtpTrend && (
                      <GradientLineChart
                        trendValues={buildRtpTrendValues(data)}
                        keyName={egmDetailKey(data)}
                        screenWidth={110}
                        isLandscape={true}
                        insightType={InsightFilterEnum.ReturnToPlayer}
                        visibleInsights={[InsightFilterEnum.ReturnToPlayer]}
                        postionLeft={310}
                        topOffset={trendTopOffset}
                      />
                    )}
                  </div>
                ))}
                <IonInfiniteScroll
                  threshold="200px"
                  disabled={isEndReached}
                  ref={ionInfiniteScrollRef}
                  onIonInfinite={handleFetchMore}
                >
                  <IonInfiniteScrollContent
                    loadingSpinner="bubbles"
                    loadingText="Loading more data..."
                  />
                </IonInfiniteScroll>
                {isEndReached && <div style={{ height: '8px' }} />}
              </div>
            </div>
          )}
        </div>
      )}
      {popoverPosition &&
        activeSlotNumber !== null &&
        createPortal(
          <>
            <div onClick={closeMenu} className="machine-menu-backdrop" />
            <div
              role="menu"
              className="machine-menu-actions"
              onClick={(e) => { e.stopPropagation(); closeMenu(); }}
              style={{ left: popoverPosition.x, top: popoverPosition.y }}
            >
              {/* Top icon row: Details | Deselect | Share */}
              <div className="cma-top-row">
                <button
                  type="button"
                  role="menuitem"
                  className="cma-icon-btn"
                  onClick={() => handleViewDetails(activeSlotNumber)}
                >
                  <span className="cma-icon-circle">
                    <InfoIcon color="rgba(255,255,255,0.70)" size={20} />
                  </span>
                  <span className="cma-icon-label">Details</span>
                </button>

                <button
                  type="button"
                  role="menuitem"
                  className="cma-icon-btn"
                  onClick={() => handleRemoveFromCompare(activeSlotNumber)}
                >
                  <span className="cma-icon-circle">
                    <IonIcon icon={checkmarkCircle} />
                  </span>
                  <span className="cma-icon-label">Deselect</span>
                </button>

                <button
                  type="button"
                  role="menuitem"
                  className="cma-icon-btn"
                  onClick={() => {
                    void handleShare(activeSlotNumber);
                  }}
                >
                  <span className="cma-icon-circle">
                    <IonIcon icon={shareSocial} />
                  </span>
                  <span className="cma-icon-label">Share</span>
                </button>
              </div>

              <hr className="cma-divider" />

              <button
                type="button"
                role="menuitem"
                className="cma-item"
                onClick={() => handleToggleFavorite(activeSlotNumber)}
              >
                <span className="cma-icon-slot" aria-hidden="true">
                  <FavoriteIcon variant="menu" />
                </span>
                {isIncludedInFavorites(activeSlotNumber, favoritesList)
                  ? 'Remove from Favorites'
                  : 'Add to Favorites'}
              </button>

              {isPlaylistEnabled && (
                <button
                  type="button"
                  role="menuitem"
                  className="cma-item"
                  onClick={() => handleTogglePlaylist(activeSlotNumber)}
                >
                  <span className="cma-icon-slot" aria-hidden="true">
                    <PlaylistIcon filled size={14} color="#94A0AA" />
                  </span>
                  {isIncludedInPlaylist(activeSlotNumber, playlist)
                    ? 'Remove from Playlist'
                    : 'Add to Playlist'}
                </button>
              )}

              {(() => {
                const activeData = hotDataList.find(
                  (d) => matchesEgmDetailId(d, activeSlotNumber)
                );
                if (!activeData) return null;
                const titleKey = getCleanGameName(activeData.game_name);
                const themeKey =
                  (activeData.game_theme && activeData.game_theme.trim()) ||
                  getCleanThemeName(activeData.game_name);
                if (!titleKey && !themeKey) return null;
                return (
                  <>
                    <hr className="cma-divider" />
                    {titleKey && (
                      <button
                        type="button"
                        role="menuitem"
                        className="cma-filter-item"
                        onClick={() => handleSearchByTitle(activeSlotNumber)}
                      >
                        <span className="cma-icon-slot" aria-hidden="true">
                          {/* <IonIcon icon={searchOutline} /> */}
                                                     <IonImg src="assets/icon/search-title.png" />

                        </span>
                        Search by &quot;Title&quot;
                      </button>
                    )}
                    {themeKey && (
                      <button
                        type="button"
                        role="menuitem"
                        className="cma-filter-item"
                        onClick={() => handleSearchByTheme(activeSlotNumber)}
                      >
                        <span className="cma-icon-slot" aria-hidden="true">
                          {/* <IonIcon icon={searchOutline} /> */}
                           <IonImg src="assets/icon/search-theme.png" />
                        </span>
                        Search by &quot;theme&quot;
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </>,
          document.body
        )}
    </IonContent>
  );
};

export default CompareAdvancedView;
